/**
 * Where an old Son of Basel portal address lands now.
 *
 * The portal drew its pages at the root of its own host (`/meetings/<id>`,
 * `/share/<token>`, `/onboarding/voice`) until 2026-10-30. Those addresses are
 * in HANDOVER, in sent emails, in Drive and in bookmarks. They are the
 * Operator module of this app now, under `/operator/<page>`, with the
 * settings and the admin pages as Settings > Operator
 * (docs/SON-OF-BASEL-MERGE.md, task 3.6). Two places answer an old address
 * and both read this one table: the API answers a page load with a 308, and
 * the web router redirects a path that reached it inside the app. One table,
 * so the two can never send the same link to different screens.
 *
 * WHAT IT WILL NOT DO is take a destination from the request. The location is
 * built from fixed strings, a page name from the lists below, and a meeting id
 * or share token that may only hold letters, digits, dashes and underscores,
 * then the request's own query. A path it does not know answers null, which
 * the API turns into a 404 and the router into Page not found, so the mapping
 * cannot be steered into an open redirect.
 *
 * TWO PAGES MOVED RATHER THAN KEPT THEIR NAME. The portal's `/talk` was the
 * link handed out for Talk's public demo, so it opens `/operator/demo`, the
 * public demo page, rather than the team's own Talk page. Its `/api-docs`
 * (Swagger UI over its API) is this app's API docs at `/api/v1/docs` now,
 * which carry Operator's routes too.
 *
 * WHAT IT LEAVES ALONE. `/`, `/settings`, `/login` and every other `/admin/*`
 * path belong to this app, so an old portal link to one of those opens this
 * app's own screen. `/forgot-password`, `/reset-password` and `/agents` were
 * dropped with the portal and are not mapped.
 *
 * No imports, so the web takes it as `@gojibasel/shared/operator-paths`
 * without pulling the kernel's Node code into the browser.
 */

/** The new homes. */
export const OPERATOR_PATH = '/operator'
export const OPERATOR_SETTINGS_PATH = '/settings/operator'
export const OPERATOR_ADMIN_PATH = '/settings/operator/admin'
/** Talk's public demo, where the old portal's `/talk` link lands. */
export const OPERATOR_DEMO_PATH = '/operator/demo'
/** This app's API docs (Swagger UI), where the old portal's `/api-docs` lands. */
export const API_DOCS_PATH = '/api/v1/docs'

/**
 * The portal's pages that keep their name under `/operator/<page>`. Each also
 * takes its own sub paths, listed in `SUB_PAGES`.
 */
export const OPERATOR_LEGACY_PAGES = ['meetings', 'share', 'ask', 'insights', 'talk', 'clone', 'onboarding', 'sandbox', 'preferences'] as const

export type OperatorLegacyPage = (typeof OPERATOR_LEGACY_PAGES)[number]

/** The onboarding steps, in the order the portal walked them. */
export const OPERATOR_ONBOARDING_STEPS = ['intro', 'identity', 'persona', 'photo', 'voice', 'calendar'] as const

/** The portal's admin pages, each a tab of Settings > Operator > Admin now. */
export const OPERATOR_ADMIN_TABS = { '/admin/users': 'users', '/admin/credentials': 'credentials' } as const

/**
 * The first path segments this table answers for, so a caller can skip it
 * for every other request without asking. `admin` and `api-docs` are here
 * because `/admin/users`, `/admin/credentials` and `/api-docs` are mapped.
 * Every other `/admin/*` path still answers null. `/signup` is not: it is
 * the public request form again, a page of this app (2026-10-30).
 */
export const OPERATOR_LEGACY_ROOTS: readonly string[] = [...OPERATOR_LEGACY_PAGES, 'admin', 'api-docs']

const PAGES: ReadonlySet<string> = new Set(OPERATOR_LEGACY_PAGES)
const STEPS: ReadonlySet<string> = new Set(OPERATOR_ONBOARDING_STEPS)

/**
 * A meeting id or a share token: a Mongo id, 32 hex characters, never a dot or
 * a slash. The API's Operator gateway reads a share token by the same rule.
 */
export const OPERATOR_TOKEN = /^[A-Za-z0-9_-]{1,128}$/
const SAFE_SEGMENT = OPERATOR_TOKEN

/** `?a=1` or `''`, from a search string with or without its `?`. */
function query(search: string): string {
  const bare = search.startsWith('?') ? search.slice(1) : search
  return bare === '' ? '' : `?${bare}`
}

/**
 * Settings > Operator > Admin on one tab, with the old link's query after it.
 * `tab` comes first and wins over a `tab` the old link carried, because the
 * address it came from named that tab by its path.
 */
export function operatorAdminTarget(tab: string, search = ''): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  params.delete('tab')
  const rest = params.toString()
  return `${OPERATOR_ADMIN_PATH}?tab=${encodeURIComponent(tab)}${rest === '' ? '' : `&${rest}`}`
}

/**
 * The page path under `/operator` for an old page and what came after it, or
 * null when the portal had no such page.
 */
function operatorPage(page: string, rest: readonly string[]): string | null {
  if (!PAGES.has(page)) return null
  if (rest.length === 0) {
    // A share link without its token was never a page. The old /talk was the
    // public demo's link, so it opens the public demo.
    if (page === 'share') return null
    return page === 'talk' ? OPERATOR_DEMO_PATH : `${OPERATOR_PATH}/${page}`
  }
  if (rest.length > 1) return null
  const sub = rest[0]!
  switch (page) {
    case 'meetings':
    case 'share':
      return SAFE_SEGMENT.test(sub) ? `${OPERATOR_PATH}/${page}/${sub}` : null
    case 'clone':
      return sub === 'meeting-signin' ? `${OPERATOR_PATH}/clone/meeting-signin` : null
    case 'onboarding':
      return STEPS.has(sub) ? `${OPERATOR_PATH}/onboarding/${sub}` : null
    default:
      return null
  }
}

/**
 * The new address for an old portal page, or null when it is not one.
 *
 *   /meetings                  -> /operator/meetings
 *   /meetings/<id>             -> /operator/meetings/<id>
 *   /share/<token>             -> /operator/share/<token> (public, no sign-in)
 *   /ask /insights             -> /operator/ask, /operator/insights
 *   /talk                      -> /operator/demo (Talk's public demo)
 *   /clone[/meeting-signin]    -> /operator/clone[/meeting-signin]
 *   /onboarding[/<step>]       -> /operator/onboarding[/<step>]
 *   /sandbox /preferences      -> /operator/sandbox, /operator/preferences
 *   /admin/users               -> /settings/operator/admin?tab=users&...
 *   /admin/credentials         -> /settings/operator/admin?tab=credentials&...
 *   /api-docs                  -> /api/v1/docs (this app's API docs)
 *
 * The query travels, except to the API docs, which read none. A trailing slash is ignored. `pathname` is the path alone,
 * `search` the query with or without its `?`.
 */
export function legacyOperatorTarget(pathname: string, search = ''): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  if (!path.startsWith('/')) return null
  if (path === '/api-docs') return API_DOCS_PATH
  const admin = (OPERATOR_ADMIN_TABS as Record<string, string>)[path]
  if (admin !== undefined) return operatorAdminTarget(admin, search)
  const [page, ...rest] = path.slice(1).split('/')
  if (page === undefined || rest.some((segment) => segment === '')) return null
  const target = operatorPage(page, rest)
  return target === null ? null : `${target}${query(search)}`
}
